import { useAccount, useConfig, useDisconnect } from '@wagmi/vue';
import type { Address } from 'viem';
import { signMessage } from '@wagmi/core';

import { defineNuxtPlugin } from '#app';
import { useProviderConfig, useSwitchValue, watch } from '#imports';
import { useUserStore } from '@/stores/user/useUserStore';

export const onMessage = (
    template: string,
    values: {
        termsLink: string;
        address: Address;
    }
): string => {
    const date = new Date();
    const time = {
        day: date.getUTCDate().toString().padStart(2, '0'),
        month: (date.getUTCMonth() + 1).toString().padStart(2, '0'),
        year: date.getUTCFullYear().toString(),
        hours: date.getUTCHours().toString().padStart(2, '0'),
        minutes: date.getUTCMinutes().toString().padStart(2, '0')
    };

    return template
        .replace('__termsLink__', values.termsLink)
        .replace('__address__', values.address)
        .replace('__day__', time.day)
        .replace('__month__', time.month)
        .replace('__year__', time.year)
        .replace('__hours__', time.hours)
        .replace('__minutes__', time.minutes);
};

export default defineNuxtPlugin({
    name: 'wagmi',
    parallel: true,
    dependsOn: ['wagmiplugin', 'init'],
    async setup() {
        const runtimeConfig = useRuntimeConfig();
        const {
            'value': isOpenSwitchModal,
            'on': onShowSwitchModal,
            'set': setVisibleSwitchModal
        } = useSwitchValue(false);

        const {
            'value': isOpenNetworkList,
            'set': setVisibleNetworkList,
            'toggle': toggleOpenNetworkList
        } = useSwitchValue(false);

        const {
            'value': isOpenWallet,
            'set': setVisibleOpenWallet,
            'toggle': toggleOpenWallet
        } = useSwitchValue(false);

        const wagmiAccount = useAccount();

        const userStore = useUserStore();

        const { access, user } = storeToRefs(userStore);

        const {
            signOut,
            signIn,
            switchNetwork,
            setNewUser
        } = userStore;

        const { disconnect } = useDisconnect();

        const { secure } = useEVMApi();
        const config = useConfig();

        const {
            isDisconnected,
            isConnected,
            address,
            chainId
        } = wagmiAccount;

        watch([address, user], ([newUserAddress, storeUser]) => {
            if (newUserAddress && storeUser) {
                setNewUser(newUserAddress);
            }
        });

        watch(
            [access],
            async ([newAccess]) => {
                if (!newAccess) {
                    disconnect();
                }
            }
        );

        watch(
            [
                isConnected,
                isDisconnected
            ],
            async ([
                connected,
                disconnected
            ]) => {
                if (disconnected) {
                    // remove({ key: QueryGBZRApiKey.USER_BALANCE });
                    signOut();
                }
                else if (connected && address.value && !userStore.user) {
                    const message = onMessage(
                        runtimeConfig.public.messageMetamask,
                        {
                            address: address.value,
                            termsLink: runtimeConfig.public.links.docs
                        }
                    );
                    const signature = await signMessage(config, {
                        message,
                        account: address.value
                    });

                    try {
                        const { data } = await secure.login({
                            address: address.value,
                            nonce: message, // TODO '1'
                            sigHex: signature // TODO '_special_smart_wallet_demo_hex' as `0x${string}`
                        });

                        if (data) {
                            signIn({
                                address: address.value as Address,
                                id: chainId.value as User['chainId'],
                                accessToken: data.access,
                                refreshToken: data.refresh
                            });
                        }
                    }
                    catch (e) {
                        console.error(e);
                        disconnect();
                    }
                }
            }
        );

        const {
            provider: { chainIds }
        } = useProviderConfig()(['contract', 'provider']);

        watch(
            chainId,
            (newChain) => {
                if (newChain) {
                    switchNetwork(newChain as User['chainId']);
                    if (!chainIds.includes(newChain as User['chainId'])) {
                        onShowSwitchModal();
                    }
                    else if (isOpenSwitchModal) {
                        setVisibleSwitchModal(false);
                    }
                }
            }
        );

        return {
            provide: {
                wagmi: {
                    account: wagmiAccount,
                    switchModal: {
                        isOpenSwitchModal,
                        setVisibleSwitchModal
                    },
                    networksList: {
                        isOpenNetworkList,
                        setVisibleNetworkList,
                        toggleOpenNetworkList
                    },
                    wallet: {
                        isOpenWallet,
                        setVisibleOpenWallet,
                        toggleOpenWallet
                    }
                },
                provider: {
                    chainIds,
                    config
                }
            }
        };
    }
});
